import { useMutation } from "@apollo/client";
import { Outlet } from "react-router";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import FlashContext from "components/FlashMessages/FlashContext";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tab from "ds/components/Tab";
import TabWithCounter from "ds/components/Tab/WithCounter";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Action, TerraformProviderVersionStatus } from "types/generated";
import { checkAllowedAction } from "utils/access";

import { FILTERS_ORDER_SETTINGS_KEY_PROVIDERS } from "../constants";
import { DELETE_PROVIDER_VERSION, REVOKE_PROVIDER_VERSION } from "../gql";
import TerraformProviderVersionStatusBadge from "../ProvidersVersionStatusBadge";
import { showProviderVersionDeleteConfirmation } from "../ProviderVersion/DeleteConfirmation";
import { DeleteProviderVersionGql, RevokeProviderVersionGql } from "../ProviderVersion/types";
import { showProviderVersionDrawer } from "../ProviderVersionDrawer";
import { showProviderVersionEditDrawer } from "../ProviderVersionEditDrawer";
import { TerraformProviderVersionWithArtifacts } from "../types";
import { ProviderVersionDocsContext } from "./Context";
import ProviderVersionDropdown from "./VersionDropdown";

const ProviderVersionDocsLayout = () => {
  const { provider, version } = useTypedContext(ProviderVersionDocsContext);

  // Convert version to expected type for drawer functions
  const versionWithArtifacts: TerraformProviderVersionWithArtifacts = {
    id: version.id,
    description: version.description,
    number: version.number,
    status: version.status,
    allowedActions: version.allowedActions,
    artifacts: "artifacts" in version ? version.artifacts : [],
    docs: { overviewContent: version.docs.overviewContent ?? "" },
  };
  const { onError, reportSuccess, reportError } = useTypedContext(FlashContext);

  const canUpdateProviderVersion = checkAllowedAction(
    version,
    Action.TerraformProviderVersionUpdate
  );
  const canRevokeProviderVersion = checkAllowedAction(
    version,
    Action.TerraformProviderVersionRevoke
  );
  const canDeleteProviderVersion = checkAllowedAction(
    version,
    Action.TerraformProviderVersionDelete
  );

  const [revokeVersion] = useMutation<RevokeProviderVersionGql>(REVOKE_PROVIDER_VERSION, {
    refetchQueries: ["GetProviderWithVersions"],
  });

  const [deleteVersion] = useMutation<DeleteProviderVersionGql>(DELETE_PROVIDER_VERSION, {
    refetchQueries: ["GetProviderWithVersions"],
  });

  const isPublished = version.status === TerraformProviderVersionStatus.Published;
  const isDraft = version.status === TerraformProviderVersionStatus.Draft;

  const handleRevokeProviderVersion = () => {
    revokeVersion({ variables: { version: version.id } })
      .then(({ data }) => {
        if (
          data?.terraformProviderVersionRevoke?.status === TerraformProviderVersionStatus.Revoked
        ) {
          reportSuccess({
            message: `Terraform provider version "${data.terraformProviderVersionRevoke.number}" was successfully revoked`,
          });
        } else {
          reportError({
            message:
              "Something went wrong while revoking Terraform provider version, please try again.",
          });
        }
      })
      .catch(onError);
  };

  const handleDeleteProviderVersion = () => {
    deleteVersion({ variables: { version: version.id } })
      .then(({ data }) => {
        if (data?.terraformProviderVersionDelete?.deleted) {
          reportSuccess({
            message: `Terraform provider version "${data.terraformProviderVersionDelete.number}" was successfully deleted`,
          });
        } else {
          reportError({
            message:
              "Something went wrong while deleting Terraform provider version, please try again.",
          });
        }
      })
      .catch(onError);
  };

  const revokeConfirmationShow = () => {
    showProviderVersionDeleteConfirmation({
      onConfirm: handleRevokeProviderVersion,
      isPublished,
      versionNumber: version.number,
    });
  };

  const deleteConfirmationShow = () => {
    showProviderVersionDeleteConfirmation({
      onConfirm: handleDeleteProviderVersion,
      isPublished,
      versionNumber: version.number,
    });
  };

  useBreadcrumbs(
    [
      {
        title: "Providers",
        link: getBreadcrumbsBackUrl("/providers", FILTERS_ORDER_SETTINGS_KEY_PROVIDERS),
      },
      {
        title: provider.id,
        link: `/provider/${provider.id}`,
      },
      {
        title: version.number,
      },
    ],
    [provider.id, version.number]
  );

  useTitle(`${provider.id} · ${version.number} · Documentation`);

  const versionBasePath = `/provider/${provider.id}/version/${version.number}`;

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper justify="between" fullWidth>
          <ViewHeaderWrapper align="center">
            <ViewHeaderTitle>{version.number}</ViewHeaderTitle>
            <TerraformProviderVersionStatusBadge status={version.status} />
          </ViewHeaderWrapper>

          <ViewHeaderWrapper align="center">
            <ProviderVersionDropdown />

            {(isPublished || isDraft) && (
              <DropdownMenuEllipsis tooltip="Version actions" dotsSize="medium">
                {(canUpdateProviderVersion.result || canUpdateProviderVersion.message) && (
                  <DropdownMenuItem
                    isDisabled={!canUpdateProviderVersion.result}
                    tooltip={canUpdateProviderVersion.message}
                    isTooltipActive={!canUpdateProviderVersion.result}
                    onAction={() => {
                      showProviderVersionEditDrawer({ providerVersion: versionWithArtifacts });
                    }}
                  >
                    Edit
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem
                  onAction={() => {
                    showProviderVersionDrawer({ providerVersion: versionWithArtifacts, provider });
                  }}
                >
                  View instructions
                </DropdownMenuItem>

                {(canDeleteProviderVersion.result || canDeleteProviderVersion.message) &&
                  !isPublished && (
                    <DropdownMenuItem
                      isDisabled={!canDeleteProviderVersion.result}
                      tooltip={canDeleteProviderVersion.message}
                      isTooltipActive={!canDeleteProviderVersion.result}
                      onAction={deleteConfirmationShow}
                      danger
                    >
                      Delete
                    </DropdownMenuItem>
                  )}

                {(canRevokeProviderVersion.result || canRevokeProviderVersion.message) &&
                  isPublished && (
                    <DropdownMenuItem
                      isDisabled={!canRevokeProviderVersion.result}
                      tooltip={canRevokeProviderVersion.message}
                      isTooltipActive={!canRevokeProviderVersion.result}
                      onAction={revokeConfirmationShow}
                      danger
                    >
                      Revoke
                    </DropdownMenuItem>
                  )}
              </DropdownMenuEllipsis>
            )}
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderNavigation>
          <Tab to={`${versionBasePath}/docs`} label="Documentation" />
          <TabWithCounter
            to={`${versionBasePath}/assets`}
            label="Assets"
            count={version.artifacts.length}
          />
          <Tab to={`${versionBasePath}/changelog`} label="Changelog" />
        </ViewHeaderNavigation>
      </ViewHeader>
      <Outlet />
    </>
  );
};

export default ProviderVersionDocsLayout;
